import { type FormEvent, useEffect, useMemo, useState } from "react";
import { RAYK_PORTRAIT_PATH, RAYK_PORTRAIT_PNG_PATH } from "./lib/portrait";
import {
  type AppointmentProposal,
  bookAppointmentProposal,
  loadAppointmentProposal,
} from "./lib/proposalRepository";
import "./appointment-proposal.css";
import "./appointment-proposal-options.css";
import "./proposal-price-display.css";

function isRaykPortraitSrc(src: string): boolean {
  return src.includes("rayk-portrait-cutout-v5");
}

function ProposalPortrait({ src, alt }: { src: string; alt: string }) {
  if (!src || isRaykPortraitSrc(src)) {
    return (
      <picture>
        <source type="image/webp" srcSet={RAYK_PORTRAIT_PATH} />
        <img src={RAYK_PORTRAIT_PNG_PATH} alt={alt} />
      </picture>
    );
  }

  return <img src={src} alt={alt} />;
}

export function AppointmentProposalPage({ token }: { token: string }) {
  const [proposal, setProposal] = useState<AppointmentProposal | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    loadAppointmentProposal(token)
      .then((result) => {
        setProposal(result);
        setSelectedOptionId(result.options.length === 1 ? (result.options[0]?.id ?? "") : "");
        const parts = result.recipientName?.trim().split(/\s+/) ?? [];
        if (parts.length > 0) setFirstName(parts[0] ?? "");
        if (parts.length > 1) setLastName(parts.slice(1).join(" "));
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Unbekannter Fehler"))
      .finally(() => setLoading(false));
  }, [token]);

  const selectedOption = useMemo(
    () => proposal?.options.find((option) => option.id === selectedOptionId) ?? null,
    [proposal, selectedOptionId],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selectedOptionId) return;
    setSubmitting(true);
    setError(null);
    try {
      await bookAppointmentProposal(token, selectedOptionId, {
        firstName,
        lastName,
        email,
        phone,
        consent,
      });
      setBooked(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Reservierung ist fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="proposal-shell proposal-loading">
        <div className="proposal-loading-mark">R</div>
        <p>Deine persönliche Einladung wird vorbereitet …</p>
      </main>
    );
  }

  if (error && !proposal) {
    return (
      <main className="proposal-shell proposal-loading">
        <div className="proposal-loading-mark">R</div>
        <h1>Diese Einladung ist gerade nicht verfügbar.</h1>
        <p>{error}</p>
      </main>
    );
  }

  if (!proposal) return null;

  const portrait = proposal.imageUrl || RAYK_PORTRAIT_PATH;
  const greetingName = firstName || proposal.recipientName || "du";
  const recipientName = proposal.recipientName || greetingName;
  const standardPrice = formatPrice(proposal.standardPriceCents, proposal.currency);
  const personalPrice = formatPrice(proposal.personalPriceCents, proposal.currency);

  if (booked) {
    return (
      <main className="proposal-shell">
        <section className="proposal-letter proposal-success">
          <BrandHeader />
          <div className="proposal-success-layout">
            <div
              className={`proposal-success-portrait${isRaykPortraitSrc(portrait) ? " is-cutout" : ""}`}
            >
              <ProposalPortrait src={portrait} alt="Rayk Schörling" />
              <span aria-hidden="true">✓</span>
            </div>
            <div className="proposal-success-copy">
              <p className="proposal-eyebrow">Termin bestätigt</p>
              <h1>Dein Gespräch ist bestätigt.</h1>
              <p className="proposal-success-lead">
                Danke, {firstName}. Diese Zeit ist jetzt fest für dich eingeplant.
              </p>
              {selectedOption ? (
                <div className="proposal-success-details">
                  <strong>{formatDate(selectedOption.startsAt)}</strong>
                  <span>
                    {formatTime(selectedOption.startsAt)}–{formatTime(selectedOption.endsAt)} Uhr
                  </span>
                  <span>{proposal.locationName ?? "Persönlicher Termin"}</span>
                </div>
              ) : null}
              <p>
                Gleich kommt eine E-Mail mit allen Details und deinem persönlichen Verwaltungslink.
              </p>
              <Signature compact />
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="proposal-shell">
      <section className="proposal-letter">
        <BrandHeader />

        <div className="proposal-hero">
          <div className="proposal-intro">
            <p className="proposal-eyebrow">Einladung zum Termin</p>
            <h1>Dein Termin mit Rayk.</h1>
            <p className="proposal-recipient">Für {recipientName}</p>
            {proposal.message ? (
              <div className="proposal-message">{proposal.message}</div>
            ) : (
              <div className="proposal-message">
                Hier ist dein persönlicher Vorschlag. Wenn alles passt, bestätige ihn direkt.
              </div>
            )}
            {selectedOption ? (
              <div className="proposal-hero-appointment">
                <strong>{formatDate(selectedOption.startsAt)}</strong>
                <span>
                  {formatTime(selectedOption.startsAt)}–{formatTime(selectedOption.endsAt)} Uhr
                </span>
                <span>
                  {proposal.locationName ?? "Persönlicher Termin"} · {proposal.variantLabel}
                </span>
              </div>
            ) : null}
            <p className="proposal-intro-note">
              Kein Suchen im Kalender. Nur die Möglichkeiten, die persönlich für dich vorbereitet
              wurden.
            </p>
          </div>

          <figure
            className={`proposal-portrait-stage${isRaykPortraitSrc(portrait) ? " is-cutout" : ""}`}
          >
            <ProposalPortrait src={portrait} alt="Rayk Schörling" />
            <figcaption>
              <span>Persönlich vorbereitet</span>
              <strong>Du buchst direkt mit Rayk.</strong>
            </figcaption>
          </figure>
        </div>

        <section className="proposal-encounter" aria-labelledby="proposal-encounter-title">
          <div className="proposal-encounter-heading">
            <p className="proposal-eyebrow">Deine Begegnung</p>
            <h2 id="proposal-encounter-title">{proposal.offerTitle}</h2>
          </div>
          <div className="proposal-encounter-facts">
            <div>
              <span>Raum</span>
              <strong>{proposal.locationName ?? "Persönlich vereinbart"}</strong>
              {proposal.addressSummary ? <small>{proposal.addressSummary}</small> : null}
            </div>
            <div>
              <span>Zeit</span>
              <strong>
                {proposal.variantLabel ?? `${proposal.durationMinutes ?? ""} Minuten`}
              </strong>
            </div>
            <div className="proposal-price-comparison">
              <div>
                <span>Standardpreis</span>
                <strong>{standardPrice ?? "Persönlich vereinbart"}</strong>
              </div>
              {personalPrice ? (
                <div className="is-personal">
                  <span>Dein persönlicher Preis</span>
                  <strong>{personalPrice}</strong>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <form onSubmit={submit} className="proposal-journey">
          <fieldset className="proposal-options-fieldset">
            <legend>
              {proposal.options.length === 1
                ? "Diese Zeit ist für dich vorgesehen"
                : "Welche Zeit passt zu dir?"}
            </legend>
            <p className="proposal-options-hint">
              {proposal.options.length === 1
                ? "Wenn alles passt, bestätige die vorbereitete Zeit direkt."
                : "Wähle die Möglichkeit, die sich für dich am stimmigsten anfühlt."}
            </p>
            <div className="proposal-options">
              {proposal.options.map((option) => {
                const selected = selectedOptionId === option.id;
                return (
                  <label
                    className={`proposal-option${selected ? " proposal-option-selected" : ""}`}
                    key={option.id}
                  >
                    <input
                      type="radio"
                      name="proposal-option"
                      value={option.id}
                      checked={selected}
                      onChange={() => {
                        setSelectedOptionId(option.id);
                        setError(null);
                      }}
                    />
                    <span className="proposal-option-copy">
                      <span className="proposal-option-date">{formatWeekday(option.startsAt)}</span>
                      <strong className="proposal-option-time">
                        {formatTime(option.startsAt)}–{formatTime(option.endsAt)} Uhr
                      </strong>
                      <span className="proposal-option-action">
                        {selected ? "Diese Zeit ist ausgewählt" : "Diese Zeit wählen"}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <p className="proposal-transition">Auswählen · Bestätigen · Ankommen</p>

          {selectedOptionId ? (
            <section className="proposal-reservation-panel" aria-live="polite">
              <div className="proposal-reservation-heading">
                <p className="proposal-eyebrow">Deine Reservierung</p>
                <h2>Termin bestätigen.</h2>
                <p>
                  Mit deiner Bestätigung ist der Termin fest gebucht. Alle weiteren Informationen
                  kommen klar und übersichtlich per E-Mail.
                </p>
              </div>

              <div className="proposal-contact-grid">
                <label>
                  <span>Vorname</span>
                  <input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>Nachname</span>
                  <input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>E-Mail für deine Bestätigung</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>Telefon, optional</span>
                  <input value={phone} onChange={(event) => setPhone(event.target.value)} />
                </label>
              </div>

              <label className="proposal-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  required
                />
                <span>
                  Ich stimme der Verarbeitung meiner Angaben zur Terminorganisation zu. Hinweise in
                  der <a href="/datenschutz">Datenschutzerklärung</a>.
                </span>
              </label>

              {error ? <p className="proposal-error">{error}</p> : null}

              <button type="submit" disabled={submitting}>
                {submitting ? "Termin wird bestätigt …" : "Termin bestätigen"}
              </button>
              <p className="proposal-trust">Persönlich · vertraulich · unkompliziert</p>
            </section>
          ) : (
            <div className="proposal-selection-prompt">Wähle oben zuerst deine passende Zeit.</div>
          )}
        </form>

        <section className="proposal-what-next">
          <p className="proposal-eyebrow">Danach</p>
          <h2>Du musst nichts weiter vorbereiten.</h2>
          <p>
            Mit deiner Reservierung erhältst du alle organisatorischen Details und einen
            persönlichen Link, über den du deinen Termin unkompliziert verwalten kannst.
          </p>
        </section>

        <Signature />
        <p className="proposal-closing-line">Verbunden. Bewusst. Schöpfen.</p>
      </section>
    </main>
  );
}

function BrandHeader() {
  return (
    <header className="proposal-brand">
      <div>
        <span className="proposal-brand-name">Rayk Schörling</span>
        <span className="proposal-brand-subtitle">Persönliche Einladung</span>
      </div>
      <span className="proposal-brand-context">Eine Einladung. Nur für dich.</span>
    </header>
  );
}

function Signature({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`proposal-signature-block${compact ? " is-compact" : ""}`}>
      <ProposalPortrait src={RAYK_PORTRAIT_PATH} alt="Rayk Schörling" />
      <div>
        <p className="proposal-signoff">Ich freue mich auf unsere Begegnung.</p>
        <p>Bis bald</p>
        <strong className="proposal-signature-name">Rayk</strong>
      </div>
    </div>
  );
}

function formatPrice(value: number | null | undefined, currency: string): string | null {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Berlin",
  }).format(new Date(value));
}

function formatWeekday(value: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Berlin",
  }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  }).format(new Date(value));
}
